import React, { useMemo, useState } from "react";
import useSWR from "swr";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { waktuCabang } from '../utils/waktuCabang';
import { apiHost } from '../utils/apiHost';
import { konfirmasi } from '../utils/dialog';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = async (url) => {
  try {
    const response = await axios.get(url, { withCredentials: true });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return { status: true, data: [] };
    }
    throw error;
  }
};

export const SerialNumber = () => {
  const { user } = useSelector((state) => state.auth);
  const baseUrl = getApiBaseUrl();
  const [openForm, setOpenForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [formData, setFormData] = useState({
    uuid: "",
    baranguuid: "",
    serialNumber: "",
    kodeBarang: "",
    status: "tersedia",
  });
  const [bulkSerials, setBulkSerials] = useState("");

  const { data: serialResponse, mutate: mutateSerials } = useSWR(`${baseUrl}/serialnumbers`, fetcher);
  const barangListUrl =
    user?.role === "admin"
      ? `${baseUrl}/barangcabang`
      : user?.role === "superadmin"
        ? `${baseUrl}/barang`
        : null;
  const { data: productResponse } = useSWR(barangListUrl, fetcher);

  const serials = useMemo(() => serialResponse?.data || [], [serialResponse]);
  const products = useMemo(() => {
    if (!user?.role || !productResponse) return [];
    if (user.role === "admin") {
      const rows = productResponse.data || [];
      const map = new Map();
      rows.forEach((row) => {
        const b = row.Barang;
        if (b?.uuid) {
          const label = `${b.namabarang || ""}${b.kodebarang ? ` (${b.kodebarang})` : ""}`.trim();
          map.set(b.uuid, { uuid: b.uuid, namabarang: label || b.namabarang });
        }
      });
      return Array.from(map.values()).sort((a, b) => a.namabarang.localeCompare(b.namabarang, "id"));
    }
    if (user.role === "superadmin") {
      const rows = productResponse.data || [];
      return rows
        .filter((b) => b?.uuid)
        .map((b) => ({
          uuid: b.uuid,
          namabarang: `${b.namabarang || ""}`.trim() || b.uuid,
        }))
        .sort((a, b) => a.namabarang.localeCompare(b.namabarang, "id"));
    }
    return [];
  }, [user?.role, productResponse]);

  const resetForm = () => {
    setFormData({
      uuid: "",
      baranguuid: "",
      serialNumber: "",
      kodeBarang: "",
      status: "tersedia",
    });
    setBulkSerials("");
    setIsEditing(false);
    setBulkMode(false);
  };

  const openCreateForm = () => {
    resetForm();
    setOpenForm(true);
  };

  const openEditForm = (serial) => {
    setFormData({
      uuid: serial.uuid,
      baranguuid: serial.baranguuid,
      serialNumber: serial.serialNumber,
      kodeBarang: serial.kodeBarang || "",
      status: serial.status || "tersedia",
    });
    setIsEditing(true);
    setBulkMode(false);
    setOpenForm(true);
  };

  const closeForm = () => {
    setOpenForm(false);
    resetForm();
  };

  const showFeedback = (type, text) => {
    setFeedback({ type, text });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.baranguuid) {
        showFeedback("error", "Barang wajib dipilih.");
        return;
      }

      if (bulkMode) {
        const parsedSerials = bulkSerials
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [serialNumber, kodeBarang] = line.split(",").map((part) => (part || "").trim());
            return { serialNumber, kodeBarang: kodeBarang || null };
          });

        if (parsedSerials.length === 0) {
          showFeedback("error", "Serial bulk tidak boleh kosong.");
          return;
        }

        await axios.post(
          `${baseUrl}/createserialnumberbulk`,
          {
            baranguuid: formData.baranguuid,
            serialNumbers: parsedSerials,
          },
          { withCredentials: true }
        );
        showFeedback("success", `${parsedSerials.length} serial number berhasil ditambahkan.`);
      } else if (isEditing) {
        await axios.put(
          `${baseUrl}/updateserialnumber/${formData.uuid}`,
          {
            serialNumber: formData.serialNumber,
            kodeBarang: formData.kodeBarang,
            status: formData.status,
          },
          { withCredentials: true }
        );
        showFeedback("success", "Serial number berhasil diupdate.");
      } else {
        await axios.post(
          `${baseUrl}/createserialnumber`,
          {
            baranguuid: formData.baranguuid,
            serialNumber: formData.serialNumber,
            kodeBarang: formData.kodeBarang,
          },
          { withCredentials: true }
        );
        showFeedback("success", "Serial number berhasil dibuat.");
      }

      await mutateSerials();
      closeForm();
    } catch (error) {
      showFeedback("error", error?.response?.data?.message || "Gagal menyimpan serial number.");
    }
  };

  const handleDelete = async (uuid) => {
    const confirmed = await konfirmasi("Hapus serial number ini?");
    if (!confirmed) return;

    try {
      await axios.delete(`${baseUrl}/deleteserialnumber/${uuid}`, { withCredentials: true });
      showFeedback("success", "Serial number berhasil dihapus.");
      await mutateSerials();
    } catch (error) {
      showFeedback("error", error?.response?.data?.message || "Gagal menghapus serial number.");
    }
  };

  const handleQuickStatusUpdate = async (uuid, status) => {
    try {
      await axios.patch(`${baseUrl}/updateserialnumberstatus/${uuid}`, { status }, { withCredentials: true });
      showFeedback("success", "Status serial number berhasil diperbarui.");
      await mutateSerials();
    } catch (error) {
      showFeedback("error", error?.response?.data?.message || "Gagal update status.");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader
          title="Serial Number Management"
          subheader={
            user?.role === "admin"
              ? `Total serial (cabang Anda): ${serials.length}`
              : user?.role === "superadmin"
                ? `Total serial (semua cabang): ${serials.length}`
                : `Total serial: ${serials.length}`
          }
          action={
            ["admin", "superadmin"].includes(user?.role) ? (
              <Button variant="contained" onClick={openCreateForm}>
                Tambah Serial
              </Button>
            ) : null
          }
        />
        <Box sx={{ px: 2, pb: 2 }}>
          {feedback.text ? <Alert severity={feedback.type}>{feedback.text}</Alert> : null}
        </Box>
        <Box sx={{ overflowX: "auto", px: 2, pb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Serial Number</TableCell>
                <TableCell>Kode Barang</TableCell>
                <TableCell>Nama Barang</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Dibuat</TableCell>
                <TableCell>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {serials.map((item) => (
                <TableRow key={item.uuid}>
                  <TableCell>{item.serialNumber}</TableCell>
                  <TableCell>{item.kodeBarang || "-"}</TableCell>
                  <TableCell>{item.Barang?.namabarang || "-"}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell>{waktuCabang(item.createdAt).toLocaleString("id-ID")}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button size="small" variant="outlined" onClick={() => openEditForm(item)}>
                        Edit
                      </Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(item.uuid)}>
                        Hapus
                      </Button>
                      <Button size="small" variant="text" onClick={() => handleQuickStatusUpdate(item.uuid, "tersedia")}>
                        Tersedia
                      </Button>
                      <Button size="small" variant="text" onClick={() => handleQuickStatusUpdate(item.uuid, "rusak")}>
                        Rusak
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {serials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography align="center">Belum ada data serial number.</Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      </Card>

      <Dialog open={openForm} onClose={closeForm} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? "Edit Serial Number" : "Tambah Serial Number"}</DialogTitle>
        <DialogContent>
          {!isEditing ? (
            <Box sx={{ mb: 2 }}>
              <Button size="small" variant={bulkMode ? "contained" : "outlined"} onClick={() => setBulkMode(true)} sx={{ mr: 1 }}>
                Bulk
              </Button>
              <Button size="small" variant={!bulkMode ? "contained" : "outlined"} onClick={() => setBulkMode(false)}>
                Single
              </Button>
            </Box>
          ) : null}

          <FormControl fullWidth margin="normal">
            <InputLabel id="barang-label">Barang</InputLabel>
            <Select
              labelId="barang-label"
              label="Barang"
              value={formData.baranguuid}
              onChange={(e) => setFormData((prev) => ({ ...prev, baranguuid: e.target.value }))}
              disabled={isEditing}
            >
              {products.map((product) => (
                <MenuItem key={product.uuid} value={product.uuid}>
                  {product.namabarang}
                </MenuItem>
              ))}
              {products.length === 0 ? (
                <MenuItem value="" disabled>
                  {user?.role === "admin" ? "Belum ada barang di cabang ini" : "Memuat barang…"}
                </MenuItem>
              ) : null}
            </Select>
          </FormControl>

          {bulkMode ? (
            <TextField
              fullWidth
              multiline
              minRows={6}
              margin="normal"
              label="Input Bulk (serial,kodeBarang per baris)"
              placeholder={"SN-001,KB-001\nSN-002,KB-001"}
              value={bulkSerials}
              onChange={(e) => setBulkSerials(e.target.value)}
            />
          ) : (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="Serial Number"
                value={formData.serialNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, serialNumber: e.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Kode Barang"
                value={formData.kodeBarang}
                onChange={(e) => setFormData((prev) => ({ ...prev, kodeBarang: e.target.value }))}
              />
              {isEditing ? (
                <FormControl fullWidth margin="normal">
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    label="Status"
                    value={formData.status}
                    onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="tersedia">tersedia</MenuItem>
                    <MenuItem value="terjual">terjual</MenuItem>
                    <MenuItem value="rusak">rusak</MenuItem>
                  </Select>
                </FormControl>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Batal</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Simpan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
